import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import userService from "../services/userService";
import Sidebar from "../components/Sidebar";
import "../styles/Profile.css";
import {
  FaUser,
  FaBriefcase,
  FaIdCard,
  FaExclamationTriangle,
  FaEdit,
  FaCheck,
  FaCopy,
  FaSpinner,
} from "react-icons/fa";
import ScratchCard from "../components/ScratchCard";
import paypalLogo from "../assets/paypal-logo.jpg";
import CustomModal from "../components/CustomModal";

function Profile() {
  const [activeTab, setActiveTab] = useState("personal");
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    companyName: "",
    website: "",
    skills: [],
    licenseKey: "",
    licenseExpiry: "",
    paypalId: "",
    upiId: ""
  });
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLicenseRevealed, setIsLicenseRevealed] = useState(false);

  // Modal State
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    onConfirm: null,
    confirmText: "OK",
    cancelText: "Cancel"
  });

  useEffect(() => {
    const fetchProfile = async () => {
      if (!auth.currentUser) {
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setProfileData({
            name: userData.name || "",
            email: userData.email || "",
            phone: userData.phone || "",
            location: userData.location || "",
            companyName: userData.companyName || "",
            website: userData.website || "",
            skills: userData.skills || [],
            licenseKey: userData.licenseKey || "",
            licenseExpiry: userData.licenseExpiry || "",
            paypalId: userData.paypalId || "",
            upiId: userData.upiId || ""
          });
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const showModal = (title, message, type = 'info', onConfirm = null, confirmText = 'OK', cancelText = 'Cancel') => {
    setModalState({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: onConfirm ? () => { onConfirm(); setModalState(prev => ({ ...prev, isOpen: false })); } : null,
      confirmText,
      cancelText
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSkillsChange = (e) => {
    const skillsArray = e.target.value.split(',').map(s => s.trim()).filter(s => s);
    setProfileData(prev => ({
      ...prev,
      skills: skillsArray
    }));
  };

  const handleSave = async () => {
    if (!auth.currentUser) return;

    setIsSaving(true);
    try {
      await userService.updateUserProfile(auth.currentUser.uid, {
        name: profileData.name,
        phone: profileData.phone,
        location: profileData.location,
        companyName: profileData.companyName,
        website: profileData.website,
        skills: profileData.skills
      });

      setIsSaving(false);
      setIsEditing(false);
      showModal("Success", "Profile updated successfully!", "success");
    } catch (error) {
      console.error("Error saving profile:", error);
      setIsSaving(false);
      showModal("Error", "Error saving profile. Please try again.", "error");
    }
  };

  const handleSaveKYC = async () => {
    if (!auth.currentUser) return;

    setIsSaving(true);
    try {
      await userService.updateUserProfile(auth.currentUser.uid, {
        paypalId: profileData.paypalId,
        upiId: profileData.upiId
      });

      setIsSaving(false);
      showModal("Success", "KYC details updated successfully!", "success");
    } catch (error) {
      console.error("Error saving KYC:", error);
      setIsSaving(false);
      showModal("Error", "Error saving KYC details.", "error");
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <Sidebar />
        <div className="profile-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <Sidebar />

      <div className="profile-container">
        <div className="profile-header">
          <h2 className="page-title">Profile</h2>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <button
            className={`tab-btn ${activeTab === "personal" ? "active" : ""}`}
            onClick={() => setActiveTab("personal")}
          >
            <FaUser /> Personal Info
          </button>
          <button
            className={`tab-btn ${activeTab === "professional" ? "active" : ""}`}
            onClick={() => setActiveTab("professional")}
          >
            <FaBriefcase /> Professional
          </button>
          <button
            className={`tab-btn ${activeTab === "license" ? "active" : ""}`}
            onClick={() => setActiveTab("license")}
          >
            <FaIdCard /> License & KYC
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === "personal" && (
            <div className="tab-panel">
              <h3>Personal Information</h3>
              <p>Update your personal details and contact information</p>

              <div className="profile-section">
                <div className="info-grid">
                  <div>
                    <label>Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={profileData.name}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <label>Email Address</label>
                    <input
                      type="text"
                      value={profileData.email}
                      disabled
                      title="Email cannot be changed"
                    />
                  </div>
                  <div>
                    <label>Phone Number</label>
                    <input
                      type="text"
                      name="phone"
                      value={profileData.phone}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <label>Location</label>
                    <input
                      type="text"
                      name="location"
                      value={profileData.location}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              </div>


              <div className="profile-actions">
                {!isEditing ? (
                  <button className="profile-action-btn edit" onClick={() => setIsEditing(true)}>
                    <FaEdit /> Edit Profile
                  </button>
                ) : (
                  <button
                    className={`profile-action-btn submit`}
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <div className="loading-spinner-small"></div>
                    ) : (
                      <>
                        Submit
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}

          {activeTab === "professional" && (
            <div className="tab-panel">
              <h3>Professional Information</h3>
              <p>Manage your business details and skills</p>

              <div className="info-grid">
                <div>
                  <label>Company Name</label>
                  <input
                    type="text"
                    name="companyName"
                    value={profileData.companyName}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </div>
                <div>
                  <label>Website (Optional)</label>
                  <input
                    type="text"
                    name="website"
                    value={profileData.website}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              </div>

              <h4 className="sub-heading">Skills & Expertise</h4>
              {isEditing ? (
                <div>
                  <input
                    type="text"
                    placeholder="Enter skills separated by commas (e.g., React, Node.js, Firebase)"
                    value={profileData.skills.join(', ')}
                    onChange={handleSkillsChange}
                    className="skills-input"
                  />
                </div>
              ) : (
                <div className="skills">
                  {profileData.skills.length > 0 ? (
                    profileData.skills.map((skill, idx) => (
                      <span key={idx}>{skill}</span>
                    ))
                  ) : (
                    <span>No skills listed</span>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === "license" && (
            <div className="tab-panel">
              <h3>License Information</h3>
              <p>View your current subscription license details</p>

              <div className="license-card" style={{
                background: 'linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)',
                color: 'white',
                padding: '2rem',
                borderRadius: '12px',
                marginTop: '1.5rem',
                boxShadow: '0 4px 20px rgba(26, 115, 232, 0.3)'
              }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', opacity: 0.8, fontSize: '0.9rem', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '1px' }}>LICENSE KEY</label>
                  <div className="license-key-display" style={{
                    display: 'inline-block',
                    minWidth: '300px',
                    textAlign: 'center',
                    position: 'relative'
                  }}>
                    <ScratchCard width={300} height={80} onReveal={() => setIsLicenseRevealed(true)}>
                      <div style={{
                        fontSize: '1.8rem',
                        fontWeight: 'bold',
                        letterSpacing: '2px',
                        fontFamily: 'SF Mono, monospace',
                        background: 'rgba(255, 255, 255, 0.15)',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        width: '100%',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '1rem'
                      }}>
                        {profileData.licenseKey || "NO ACTIVE LICENSE"}
                        {isLicenseRevealed && profileData.licenseKey && (
                          <FaCopy
                            style={{ cursor: 'pointer', fontSize: '1.2rem', opacity: 0.8 }}
                            title="Copy License Key"
                            onClick={() => {
                              navigator.clipboard.writeText(profileData.licenseKey);
                              showModal("Copied", "License key copied to clipboard!", "success");
                            }}
                          />
                        )}
                      </div>
                    </ScratchCard>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', opacity: 0.8, fontSize: '0.9rem', marginBottom: '0.5rem' }}>EXPIRY DATE</label>
                  <div style={{ fontSize: '1.2rem', fontWeight: '500' }}>
                    {profileData.licenseExpiry ? new Date(profileData.licenseExpiry).toLocaleDateString(undefined, {
                      year: 'numeric', month: 'long', day: 'numeric'
                    }) : "N/A"}
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                    {profileData.licenseExpiry && (
                      <button
                        onClick={() => window.open('https://pages.razorpay.com/stores/st_RbNq9z8lf79jWw', '_blank')}
                        className="license-btn-renew"
                      >
                        Renew License
                      </button>
                    )}
                    <button
                      onClick={() => window.open('https://pages.razorpay.com/stores/st_RbNq9z8lf79jWw', '_blank')}
                      className="license-btn-upgrade"
                    >
                      Upgrade Subscription
                    </button>
                  </div>


                  {profileData.licenseExpiry && (
                    <div style={{ marginTop: '1.5rem', textAlign: 'center', background: 'rgba(255,255,255,0.1)', padding: '1rem', borderRadius: '8px' }}>
                      <span style={{ fontSize: '0.9rem', opacity: 0.9 }}>Days Remaining</span>
                      <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                        {Math.max(0, Math.ceil((new Date(profileData.licenseExpiry) - new Date()) / (1000 * 60 * 60 * 24)))} Days
                      </div>
                    </div>
                  )}

                  {profileData.licenseExpiry && new Date(profileData.licenseExpiry) < new Date() && (
                    <div style={{
                      marginTop: '1.5rem',
                      background: 'rgba(255,0,0,0.2)',
                      padding: '0.8rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <FaExclamationTriangle /> License Expired. Please renew to access projects.
                    </div>
                  )}
                </div>
              </div>

              <div className="kyc-section" style={{ marginTop: '3rem', borderTop: '1px solid #eee', paddingTop: '2rem' }}>
                <h3>KYC Information</h3>
                <p>Please provide your payment details for payouts.</p>

                <div className="kyc-grid" style={{ display: 'grid', gap: '1.5rem', marginTop: '1.5rem' }}>

                  {/* PayPal Section */}
                  <div className="kyc-item" style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                    <div style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9f9f9', borderRadius: '50%' }}>
                      <img
                        src={paypalLogo}
                        alt="PayPal"
                        style={{ width: '40px' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#003087' }}>PayPal Email</label>
                      <input
                        type="email"
                        name="paypalId"
                        value={profileData.paypalId}
                        onChange={handleInputChange}
                        placeholder="Enter your PayPal email"
                        style={{ width: '100%', padding: '0.8rem', borderRadius: '6px', border: '1px solid #ccc' }}
                      />
                    </div>
                  </div>



                  <div style={{ textAlign: 'center', position: 'relative', margin: '0.5rem 0' }}>
                    <span style={{ background: '#fff', padding: '0 1rem', color: '#666', fontWeight: '500', position: 'relative', zIndex: 1 }}>OR</span>
                    <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', background: '#e0e0e0', zIndex: 0 }}></div>
                  </div>

                  {/* UPI Section */}
                  <div className="kyc-item" style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                    <div style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9f9f9', borderRadius: '50%' }}>
                      <img
                        src="https://upload.wikimedia.org/wikipedia/commons/e/e1/UPI-Logo-vector.svg"
                        alt="UPI"
                        style={{ width: '40px' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#4caf50' }}>UPI ID</label>
                      <input
                        type="text"
                        name="upiId"
                        value={profileData.upiId}
                        onChange={handleInputChange}
                        placeholder="Enter your UPI ID (e.g., name@upi)"
                        style={{ width: '100%', padding: '0.8rem', borderRadius: '6px', border: '1px solid #ccc' }}
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleSaveKYC}
                    disabled={isSaving}
                    className={`kyc-btn-submit`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      opacity: (isSaving) ? 0.8 : 1,
                      cursor: (isSaving) ? 'default' : 'pointer'
                    }}
                  >
                    {isSaving ? (
                      <>
                        <FaSpinner className="icon-spin" /> Saving...
                      </>
                    ) : (
                      <>
                        <FaCheck /> Submit KYC Details
                      </>
                    )}
                  </button>

                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <CustomModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        onConfirm={modalState.onConfirm}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
      />
    </div>
  );
}

export default Profile;
